import React from 'react'
import io from 'socket.io-client'
import { eventChannel } from 'redux-saga'
import {
	take,
	takeLatest,
	call,
	put,
	select,
	race,
	cancelled,
	delay,
} from 'redux-saga/effects'
import { toastr } from 'react-redux-toastr'
import { useTranslate } from 'react-polyglot'

import ReloadPageSvg from 'assets/img/svg/reload-page.js'
import { Icon, FlexContainer, Text, Button } from 'components'

import EE from 'emitter.js'

import { articleActions } from '../articles'
import { getAchievement } from '../achievements/types'
import { socketTypes } from '.'

const getToken = state => state.auth.token
const getUserId = state => state.auth.id
const getCurrentArticleId = state => state.articles.article.id

// Wrapping functions for socket events (connect, disconnect, reconnect)
let socket

const AppUpdateComponent = props => {
	const t = useTranslate()

	return (
		<>
			<FlexContainer alignItems="flex-start" justifyContent="flex-start">
				<ReloadPageSvg
					style={{
						width: '2.5rem',
						minWidth: '2.5rem',
						height: 'auto',
					}}
				/>
				<>
					<Text weight="bold" color="dark">
						{t(`app_update_title`)}
					</Text>
					<Text size="small" color="dark">
						{t(`app_update_text`)}
					</Text>
					<Text color="dark">
						<Button
							onClick={() => {
								location.reload(true)
							}}
							text={t(`app_update_button`)}
							layout="secondary"
							size="small"
						/>
					</Text>
				</>
			</FlexContainer>
			<Button
				className="rrt-close-button"
				onClick={e => {
					e.preventDefault()
					e.stopPropagation()

					props.remove()
				}}
				centerContent
				icon={<Icon name="close" />}
			/>
		</>
	)
}

const AchievementComponent = props => {
	const t = useTranslate()

	return (
		<>
			<FlexContainer alignItems="flex-start" justifyContent="flex-start">
				<img
					style={{
						width: '2.5rem',
						minWidth: '2.5rem',
						height: 'auto',
					}}
					src={props.achievement.image}
					alt=""
				/>
				<>
					<Text weight="bold" color="dark">
						{t(`achievement_unlocked`)}
					</Text>
					<Text size="small" color="dark">
						{t(`achievements.${props.achievement.type}.title`)}
					</Text>
				</>
			</FlexContainer>
			<Button
				className="rrt-close-button"
				onClick={e => {
					e.preventDefault()
					e.stopPropagation()

					props.remove()
				}}
				centerContent
				icon={<Icon name="close" />}
			/>
		</>
	)
}

const connect = token => {
	socket = io(`${process.env.WS}`, {
		path: '/app',
	})

	return new Promise(resolve => {
		socket.on('connect', () => {
			// console.log('Connected …')

			socket.emit('authenticate', { token })

			resolve(socket)
		})
	})
}

const subscribe = (s, user) =>
	eventChannel(emit => {
		s.on('authenticated', () => {
			// console.log('Sockets: Authenticated …')
		})
			.on('unauthorized', msg => {
				// console.log(
				// 	`Sockets: Unauthorized, ${JSON.stringify(msg.data)}`
				// )
			})
			.on('disconnect', () => {
				// console.log('Sockets: Disconnected …')
				// socket.disconnect()
			})

			.on('appUpdate', () => {
				toastr.light('', {
					timeOut: 0,
					removeOnHover: false,
					showCloseButton: false,
					closeOnToastrClick: true,
					component: AppUpdateComponent,
				})
			})
			.on('articleUpdate', data => {
				emit(articleActions.wsArticleUpdate(user, data))
			})
			.on('articlesChanged', data => {
				emit(articleActions.updateArticlesSuccess(data))
			})
			.on('slugChanged', newSlug => {
				const article = location.pathname.substring(
					location.pathname.split('/', 2).join('/').length + 1,
					location.pathname.length
				)
				const hash = location.hash || ''
				const url = `/${newSlug}/${article}${hash}`

				EE.emit('showSlugChangedModal', { url })
			})
			.on('achievementUnlocked', data => {
				// console.log('achievementUnlocked', data)

				const achievement = getAchievement(data.type)

				if (achievement.spotlight) {
					EE.emit('showAchievementsModal', getAchievement(data.type))
				} else {
					toastr.light('', {
						timeOut: 10000,
						removeOnHover: false,
						showCloseButton: false,
						closeOnToastrClick: true,
						onToastrClick: () => {
							EE.emit(
								'showAchievementsModal',
								getAchievement(data.type)
							)
						},
						component: props => (
							<AchievementComponent
								achievement={achievement}
								{...props}
							/>
						),
					})
				}
			})

		// .on('userOpenedArticle', (msg) => {
		// 	console.log(`userOpenedArticle | User: ${msg.user} | Article: ${msg.article} `)
		// })
		// .on('userLeftArticle', (msg) => {
		// 	console.log(`userLeftArticle | User: ${msg.user} | Article: ${msg.article} `)
		// })

		// return () => {
		// 	s.off('userOpenedArticle', emit)
		// }

		return () => {}
	})

// Saga to switch on channel
function* listenServer() {
	try {
		const token = yield select(getToken)

		// https://redux-saga.js.org/docs/advanced/RacingEffects.html
		const { s, timeout } = yield race({
			s: call(connect, token),
			timeout: delay(1500),
		})

		// if (timeout) console.log('Sockets: Connection timed out …')

		const user = yield select(getUserId)
		const channel = yield call(subscribe, s, user)

		while (true) {
			const action = yield take(channel)
			yield put(action)

			// console.log(`Sockets: Action, `, action)
		}
	} catch (error) {
		// console.log(`Sockets: Error, `, error)
	} finally {
		if (yield cancelled()) {
			socket.disconnect(true)
		}
	}
}

// Saga listens for start and stop actions
function* startStopChannel() {
	yield race({
		task: call(listenServer),
		cancel: take(socketTypes.STOP_CHANNEL),
	})
}

const socketSagas = [takeLatest(socketTypes.START_CHANNEL, startStopChannel)]

export default socketSagas
